import React, { CSSProperties, FC, useMemo, useCallback } from "react";
import { Link as RouterLink, LinkProps } from "react-router-dom";
import { useHover } from "react-use";
import { LocationDescriptor, LocationState } from "history";
import { StrokeLine } from "../stroke";
import { useSettings } from "@montana/containers/settings/settings.container";
import { useTracking } from "@montana/containers/tracking";
import { TrackingTypes } from "@montana/helpers/tracking.helper";
import { isBrowser } from "@montana/helpers/browser.helper";
import { omit } from "lodash-es";
import { trackCustomEvent as dataLayerPush } from "@montana/services/tracking";
import { useDynamicSettings } from "@montana/containers/dynamic-settings";
import {removeTrailingSlash} from "@montana/helpers/location.helper";

export interface IOptionalSome<S = LocationState>
    extends Omit<LinkProps, "to"> {
    to?: LocationDescriptor<S>;
    style?: CSSProperties;
    withStroke?: boolean;
    strokeActive?: boolean;
    inverseStrokeAnimation?: boolean;
    trackingType?: TrackingTypes;
    trackingAction?: string;
    trackingPayload?: { [key: string]: string };
    insametab?: boolean | undefined;
}

const DEFAULT_CLASS_NAME = "link";
const DEFAULT_REL = "noopener noreferrer";
const DEFAULT_TARGET = "_blank";
const IS_ABSOLUTE = /^https?:|mailto:|tel:/;
const NO_HREF_CLASS_NAME = "link--no-href";
const OMIT_FROM_DOM = [
    "inverseStrokeAnimation",
    "strokeActive",
    "to",
    "trackingAction",
    "trackingPayload",
    "trackingType",
    "withStroke",
    "insametab"
];

export const Link: FC<IOptionalSome> = props => {
    const { market } = useSettings();
    const { languages } = useDynamicSettings();
    const { trackCustomEvent } = useTracking();

    const isAbsoluteUrl = useMemo(
        () => !isBrowser || IS_ABSOLUTE.test(props.to as string),
        [props.to]
    );

    const domProps = useMemo<IOptionalSome>(() => {
        const classNames = [DEFAULT_CLASS_NAME, props.className];
        if (!props.to) classNames.push(NO_HREF_CLASS_NAME);

        return isAbsoluteUrl
            ? {
                ...omit(props, OMIT_FROM_DOM),
                className: classNames.join(" "),
                rel: DEFAULT_REL,
                target: props.target || DEFAULT_TARGET
            }
            : {
                ...omit(props, OMIT_FROM_DOM),
                className: classNames.join(" ")
            };
    }, [isAbsoluteUrl, props]);

    const trackHandler = useCallback(() => {
        // This should happen in addition to the other events, hence not part of the switch
        if (
            props.trackingType === TrackingTypes.Other &&
            props.trackingPayload
        ) {
            dataLayerPush(props.trackingPayload);
        }

        const currentLocation = document.location.href;
        const targetLocation = props.to as string;

        switch (props.trackingType) {
            case TrackingTypes.Social:
            case TrackingTypes.Download:
            case TrackingTypes.Contact: {
                const action = props.trackingAction || targetLocation;
                trackCustomEvent(props.trackingType, action, currentLocation);
                break;
            }
            default:
                trackCustomEvent(
                    TrackingTypes.OutgoingLink,
                    targetLocation,
                    currentLocation
                );
                break;
        }
    }, [
        props.to,
        props.trackingAction,
        props.trackingPayload,
        props.trackingType
    ]);

    const parsedTo = useMemo(() => {
        const to = props.to as string;

        return removeTrailingSlash(to);
    }, [market, languages, props.to]);

    const contentRenderer = useCallback(
        (isHovering: boolean) => (
            <>
                {props.children}
                {props.withStroke && (
                    <StrokeLine
                        active={props.strokeActive || isHovering}
                        className="link__underline"
                        inverseAnimation={!!props.inverseStrokeAnimation}
                    />
                )}
            </>
        ),
        [
            props.children,
            props.inverseStrokeAnimation,
            props.strokeActive,
            props.withStroke
        ]
    );

    const linkRenderer = useCallback(
        (isHovering: boolean) => {
            const content = contentRenderer(isHovering);
            const renderAsRouteLink = !!parsedTo && !isAbsoluteUrl;

            // If no href, we simply render a span to avoid too many breaches on accessibility
            if (!parsedTo) {
                return (
                    <span className={domProps.className}>
                        {content}
                    </span>
                );
            }

            return renderAsRouteLink ? (
                props.insametab
                    ? <RouterLink to={parsedTo} {...domProps} target="_self">
                        {content}
                    </RouterLink>
                    : <RouterLink to={parsedTo} {...domProps}>
                        {content}
                    </RouterLink>

            ) : (
                <a href={parsedTo} {...domProps} onClick={trackHandler}>
                    {content}
                </a>
            );
        },
        [contentRenderer, domProps, isAbsoluteUrl, parsedTo, trackHandler]
    );

    const [linkElement] = useHover(linkRenderer);
    return linkElement;
};
